import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, Loader2, PartyPopper, TriangleAlert } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSiteTheme } from '../hooks/useSiteTheme';
import { usePageScroll } from '../hooks/usePageScroll';
import { completeCheckout, syncBilling, takeAfterSubscribe } from '../lib/billingApi';
import { apiErrorMessage } from '../lib/projectApi';
import { SiteHeader } from '../components/billing/SiteHeader';
import { WannaPublishLockup } from '../components/editor/WannaPublishLogo';
import { Button } from '../components/ui/button';

const heading = { fontFamily: 'Outfit, Inter, sans-serif' };
const ATTEMPTS = 6;
const WAIT_MS = 1500;

/**
 * Stripe sends the browser here after Checkout (?session_id=…). The backend reads the session and stores the
 * subscription (so the editor opens even if the webhook is late), then we continue to the editor — or to the share
 * link the person originally followed. A session that is not finished yet is re-checked a few times before we give up
 * and offer a manual retry.
 */
export default function SubscribeSuccess() {
  const { refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [theme, toggleTheme] = useSiteTheme();
  usePageScroll(); // index.css hides body overflow for the editor; this page scrolls like a normal web page
  const [phase, setPhase] = useState('working'); // 'working' | 'done' | 'pending' | 'error'
  const [message, setMessage] = useState('');
  const started = useRef(false);

  const finish = async (billing) => {
    await refresh();
    setPhase('done');
    const trial = billing?.status === 'trialing';
    toast.success(trial ? 'Your free trial has started' : 'You are subscribed', { description: trial ? 'Enjoy everything WannaPublish has to offer. Your card is not charged until the trial ends.' : 'Thanks for subscribing to WannaPublish.', id: 'checkout', duration: 6000 });
    setTimeout(() => navigate(takeAfterSubscribe(), { replace: true }), 900);
  };

  const confirm = async () => {
    const sessionId = new URLSearchParams(location.search).get('session_id');
    if (!sessionId) { navigate('/subscribe', { replace: true }); return; }
    setPhase('working');
    setMessage('');
    try {
      for (let i = 0; i < ATTEMPTS; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const res = await completeCheckout(sessionId);
        if (res.complete && res.allows) { await finish(res.billing); return; }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, WAIT_MS));
      }
      const synced = await syncBilling();
      if (synced.allows) { await finish(synced.billing); return; }
      setPhase('pending');
    } catch (e) {
      setMessage(await apiErrorMessage(e, 'Your subscription could not be confirmed.'));
      setPhase('error');
    }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    confirm();
    // once on arrival (started ref): confirm() reads the session id of the URL we arrived with; it is also what the Retry
    // button calls, so it is deliberately a plain function rather than a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="min-h-[100dvh] flex flex-col bg-background text-foreground" data-testid="subscribe-success-page" data-theme={theme} data-phase={phase}>
      <SiteHeader theme={theme} onToggleTheme={toggleTheme} testId="success-header" />
      <section className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-[460px] rounded-xl border border-border bg-card p-6 sm:p-8 shadow-sm text-center" role="status" aria-live="polite" data-testid="subscribe-success-card">
          <div className="flex justify-center"><WannaPublishLockup size={44} pulse={phase === 'working'} /></div>
          {phase === 'working' && (
            <>
              <h1 className="mt-5 text-xl font-semibold tracking-tight" style={heading} data-testid="success-title">Confirming with Stripe…</h1>
              <p className="mt-2 text-sm text-muted-foreground">One moment while we switch on your subscription.</p>
              <Loader2 className="mx-auto mt-5 h-6 w-6 animate-spin text-primary" aria-hidden="true" />
            </>
          )}
          {phase === 'done' && (
            <>
              <h1 className="mt-5 text-xl font-semibold tracking-tight flex items-center justify-center gap-2" style={heading} data-testid="success-title"><PartyPopper className="h-5 w-5 text-primary" aria-hidden="true" />You are in</h1>
              <p className="mt-2 text-sm text-muted-foreground">Opening the editor…</p>
            </>
          )}
          {phase === 'pending' && (
            <>
              <h1 className="mt-5 text-xl font-semibold tracking-tight" style={heading} data-testid="success-title">Almost there</h1>
              <p className="mt-2 text-sm text-muted-foreground">Stripe has not finished setting up the subscription yet. This usually takes a few seconds — check again, or come back from the plans page.</p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <Button type="button" className="h-10" onClick={confirm} data-testid="success-retry">Check again</Button>
                <Button type="button" variant="outline" className="h-10" onClick={() => navigate('/subscribe', { replace: true })} data-testid="success-back-to-plans">Back to plans<ArrowRight className="h-4 w-4 ml-1.5" aria-hidden="true" /></Button>
              </div>
            </>
          )}
          {phase === 'error' && (
            <>
              <h1 className="mt-5 text-xl font-semibold tracking-tight flex items-center justify-center gap-2" style={heading} data-testid="success-title"><TriangleAlert className="h-5 w-5 text-destructive" aria-hidden="true" />Something went wrong</h1>
              <p className="mt-2 text-sm text-muted-foreground" data-testid="success-error">{message}</p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <Button type="button" className="h-10" onClick={confirm} data-testid="success-retry">Try again</Button>
                <Button type="button" variant="outline" className="h-10" onClick={() => navigate('/subscribe', { replace: true })} data-testid="success-back-to-plans">Back to plans</Button>
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
